import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  PARTICLE_BLE_WIFI_RX_CHARACTERISTIC_UUID,
  PARTICLE_BLE_WIFI_SERVICE_UUID,
  PARTICLE_BLE_WIFI_TX_CHARACTERISTIC_UUID,
  ParticleBleWifiClient,
  WIFI_SETUP_LAST_SSID_KEY,
  buildWifiCredentialsPayload,
  formatBlePayload,
} from '../utils/particleBleWifi';

const statusTone = {
  idle: 'secondary',
  connecting: 'info',
  connected: 'success',
  sending: 'warning',
  success: 'success',
  error: 'danger',
};

export function getBluetoothSupportState() {
  if (!window.isSecureContext) {
    return {
      ok: false,
      message: 'Web Bluetooth доступен только на HTTPS или localhost.',
    };
  }
  if (!navigator.bluetooth) {
    return {
      ok: false,
      message: 'Этот браузер не поддерживает Web Bluetooth. Используйте Chrome или Edge.',
    };
  }
  return { ok: true, message: '' };
}

export const WifiSetupPanel = forwardRef(function WifiSetupPanel({ compact = false, onSetupComplete }, ref) {
  const clientRef = useRef(null);
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('idle');
  const [deviceName, setDeviceName] = useState('');
  const [message, setMessage] = useState('');
  const [notifications, setNotifications] = useState([]);

  const bluetoothSupport = useMemo(getBluetoothSupportState, []);
  const previewPayload = useMemo(() => {
    try {
      return ssid ? formatBlePayload(buildWifiCredentialsPayload(ssid, password)) : '';
    } catch (_) {
      return '';
    }
  }, [ssid, password]);

  const getClient = () => {
    if (!clientRef.current) {
      clientRef.current = new ParticleBleWifiClient({
        onNotification: (notification) => {
          setNotifications((prev) => [
            {
              id: `${Date.now()}-${prev.length}`,
              ...notification,
              receivedAt: new Date().toLocaleTimeString(),
            },
            ...prev,
          ].slice(0, 8));
        },
        onDisconnected: () => {
          setStatus('idle');
          setMessage('BLE-контроллер отключен.');
          setDeviceName('');
        },
      });
    }
    return clientRef.current;
  };

  useEffect(() => () => clientRef.current?.disconnect(), []);

  const connect = async () => {
    setStatus('connecting');
    setMessage('Поиск контроллеров...');
    try {
      const device = await getClient().connect();
      setDeviceName(device.name || device.id || 'Particle BLE controller');
      const savedSsid = localStorage.getItem(WIFI_SETUP_LAST_SSID_KEY);
      if (savedSsid) {
        setSsid((current) => current || savedSsid);
      }
      setStatus('connected');
      setMessage('Контроллер подключен. Заполните Wi‑Fi credentials.');
    } catch (err) {
      console.error('BLE Error: ', err);
      setStatus('error');
      setMessage(err?.message || 'Не удалось подключиться по BLE.');
    }
  };

  const submitWifiCredentials = async (event) => {
    event.preventDefault();
    if (!ssid.trim()) {
      setStatus('error');
      setMessage('Введите SSID.');
      return;
    }

    setStatus('sending');
    setMessage('Передаем credentials и отправляем команду перезагрузки Wi‑Fi модуля...');
    try {
      const { rebootPayload } = await getClient().setWifiCredentials(ssid, password);
      setStatus('success');
      setMessage('Настройки приняты, контроллер перезагружается...');
      localStorage.setItem(WIFI_SETUP_LAST_SSID_KEY, ssid.trim());
      setPassword('');
      onSetupComplete?.({ rebootPayload });
    } catch (err) {
      console.error('BLE Error: ', err);
      setStatus('error');
      setMessage(err?.message || 'Не удалось отправить Wi‑Fi credentials.');
    }
  };

  const disconnect = () => {
    clientRef.current?.disconnect();
    setStatus('idle');
    setDeviceName('');
    setMessage('BLE-соединение закрыто.');
  };

  useImperativeHandle(ref, () => ({
    startDiscovery: connect,
    disconnect,
  }));

  const busy = status === 'connecting' || status === 'sending';
  const connected = Boolean(deviceName);

  return (
    <div className={compact ? '' : 'container mt-3'}>
      <div className="row justify-content-center">
        <div className={compact ? 'col-12' : 'col-12 col-xl-9'}>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
            <div>
              <h1 className="h3 fw-bold mb-1">Настройка Wi‑Fi контроллера через Particle BLE</h1>
              <p className="text-muted mb-0">
                Подключение к Particle BLE Control Protocol и передача WPA2 credentials в бинарном формате.
              </p>
            </div>
            <span className={`badge text-bg-${statusTone[status] || 'secondary'}`}>
              {status === 'idle' ? 'Ожидание' : status}
            </span>
          </div>

          {!bluetoothSupport.ok ? (
            <div className="alert alert-warning">{bluetoothSupport.message}</div>
          ) : null}

          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <h2 className="h5 mb-3">BLE параметры</h2>
              <dl className="row small mb-0">
                <dt className="col-sm-4">Service UUID</dt>
                <dd className="col-sm-8 font-monospace">{PARTICLE_BLE_WIFI_SERVICE_UUID}</dd>
                <dt className="col-sm-4">TX Characteristic, запись</dt>
                <dd className="col-sm-8 font-monospace">{PARTICLE_BLE_WIFI_TX_CHARACTERISTIC_UUID}</dd>
                <dt className="col-sm-4">RX Characteristic, уведомления</dt>
                <dd className="col-sm-8 font-monospace">{PARTICLE_BLE_WIFI_RX_CHARACTERISTIC_UUID}</dd>
              </dl>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <h2 className="h5 mb-1">Подключение и credentials</h2>
                  <div className="text-muted small">
                    {deviceName ? `Подключено: ${deviceName}` : 'Контроллер не выбран'}
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={connect}
                    disabled={!bluetoothSupport.ok || busy}
                  >
                    {status === 'connecting' ? 'Поиск контроллеров...' : 'Настроить Wi‑Fi'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={disconnect}
                    disabled={busy || !deviceName}
                  >
                    Отключить
                  </button>
                </div>
              </div>

              {message ? (
                <div className={`alert alert-${statusTone[status] || 'secondary'} py-2`} role="status">
                  {message}
                </div>
              ) : null}

              {connected ? (
                <form onSubmit={submitWifiCredentials}>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="wifi-ssid">
                      SSID
                    </label>
                    <input
                      id="wifi-ssid"
                      type="text"
                      className="form-control"
                      value={ssid}
                      onChange={(event) => setSsid(event.target.value)}
                      autoComplete="off"
                      maxLength={255}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="wifi-password">
                      Пароль WPA2
                    </label>
                    <input
                      id="wifi-password"
                      type="password"
                      className="form-control"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="new-password"
                      maxLength={255}
                    />
                    <div className="form-text">Первый байт security type всегда равен 3 (WPA2).</div>
                  </div>
                  {previewPayload ? (
                    <div className="mb-3">
                      <div className="small text-muted mb-1">Payload preview</div>
                      <code className="d-block bg-body-tertiary rounded p-2 text-break">{previewPayload}</code>
                    </div>
                  ) : null}
                  <button type="submit" className="btn btn-primary" disabled={!bluetoothSupport.ok || busy}>
                    {status === 'sending' ? 'Отправляем...' : 'Отправить Wi‑Fi credentials'}
                  </button>
                </form>
              ) : (
                <div className="alert alert-light border mb-0">
                  Нажмите «Настроить Wi‑Fi», выберите контроллер в Bluetooth-окне браузера, и форма появится сразу
                  после подключения.
                </div>
              )}
            </div>
          </div>

          {notifications.length > 0 ? (
            <div className="card shadow-sm mt-3">
              <div className="card-body">
                <h2 className="h5 mb-3">RX уведомления</h2>
                <div className="list-group list-group-flush">
                  {notifications.map((item) => (
                    <div className="list-group-item px-0" key={item.id}>
                      <div className="small text-muted">{item.receivedAt}</div>
                      <div className="font-monospace small text-break">{item.hex}</div>
                      {item.text ? <div className="small text-break">{item.text}</div> : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default function WifiSetupPage() {
  const { token } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  return <WifiSetupPanel />;
}
